import { useCallback, useEffect, useState } from 'react'

type PasswordRule = {
  code?: string
  message?: string
  status?: 'valid' | 'invalid' | 'pending'
}

type ResetPasswordSdk = {
  resetPassword: (data: {
    'password-reset': string
    're-enter-password': string
  }) => Promise<void>
  validatePassword?: (password: string) => {
    isValid?: boolean
    results?: PasswordRule[]
  }
  screen?: {
    data?: { username?: string }
    texts?: {
      title?: string
      description?: string
      buttonText?: string
      passwordPlaceholder?: string
    }
  }
  transaction?: {
    passwordPolicy?: { minLength?: number } | null
  }
  backToLogin?: () => Promise<void>
  getErrors?: () => Array<{ code?: string; message?: string }>
}

export type ResetPasswordOutcome = 'submitted' | 'preview'

/** Erros de validação local — a tela decide o texto a partir do código. */
export type ResetPasswordError =
  | { code: 'WEAK_PASSWORD'; minLength: number }
  | { code: 'MISMATCH' }
  | { code: 'POLICY'; rules: PasswordRule[] }
  | { code: 'EXPIRED_LINK' }
  | { code: 'FAILED'; cause?: unknown }

const MIN_LENGTH_FALLBACK = 8
const EXPIRED_HINTS = /expired|invalid.?ticket|not.?found|already.?used/i

async function createSdk(): Promise<ResetPasswordSdk | null> {
  try {
    const mod = await import('@auth0/auth0-acul-js/reset-password')
    return new mod.default() as unknown as ResetPasswordSdk
  } catch {
    return null
  }
}

/**
 * Regras puras de senha, testáveis sem DOM.
 * A política do tenant vem em `validatePassword`; quando o SDK devolve
 * `isValid: false` ou regra `status: 'invalid'`, a senha não vale.
 */
export function evaluatePassword(
  password: string,
  confirm: string,
  minLength = MIN_LENGTH_FALLBACK,
  sdkValidation?: { isValid?: boolean; results?: PasswordRule[] },
): ResetPasswordError | null {
  if (password.length < minLength) return { code: 'WEAK_PASSWORD', minLength }
  if (password !== confirm) return { code: 'MISMATCH' }
  const broken = (sdkValidation?.results ?? []).filter(
    (rule) => rule.status === 'invalid',
  )
  if (sdkValidation?.isValid === false) return { code: 'POLICY', rules: broken }
  if (broken.length > 0) return { code: 'POLICY', rules: broken }
  return null
}

const POLICY_HINTS = /password|policy|weak|strength|history|dictionary|senha/i

/**
 * Falha devolvida pelo Auth0 no re-render da tela (o envio é um POST do
 * Universal Login, não uma exceção no browser). Vira um código da UI, sem
 * repassar a mensagem do provedor.
 */
export function classifyServerErrors(
  errors: Array<{ code?: string; message?: string }> | null | undefined,
): ResetPasswordError | null {
  if (!errors?.length) return null
  const text = errors.map((e) => `${e.code ?? ''} ${e.message ?? ''}`).join(' ')
  if (EXPIRED_HINTS.test(text)) return { code: 'EXPIRED_LINK' }
  if (POLICY_HINTS.test(text)) return { code: 'POLICY', rules: [] }
  return { code: 'FAILED', cause: errors }
}

export function classifySubmitError(error: unknown): ResetPasswordError {
  const message = error instanceof Error ? error.message : String(error)
  if (EXPIRED_HINTS.test(message)) return { code: 'EXPIRED_LINK' }
  return { code: 'FAILED', cause: error }
}

export function useResetPasswordManager() {
  const [sdk, setSdk] = useState<ResetPasswordSdk | null>(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const instance = await createSdk()
      if (!cancelled) {
        setSdk(instance)
        setLoaded(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const minLength =
    sdk?.transaction?.passwordPolicy?.minLength ?? MIN_LENGTH_FALLBACK

  const handleSubmit = useCallback(
    async (password: string, confirm: string): Promise<ResetPasswordOutcome> => {
      const instance = sdk ?? (await createSdk())
      const validation = instance?.validatePassword?.(password)
      const invalid = evaluatePassword(
        password,
        confirm,
        instance?.transaction?.passwordPolicy?.minLength ?? minLength,
        validation,
      )
      if (invalid) {
        const err = new Error(invalid.code) as Error & {
          detail?: ResetPasswordError
        }
        err.detail = invalid
        throw err
      }

      if (instance?.resetPassword) {
        await instance.resetPassword({
          'password-reset': password,
          're-enter-password': confirm,
        })
        return 'submitted'
      }

      console.info('[preview reset-password]', { passwordLength: password.length })
      window.dispatchEvent(
        new CustomEvent('alelo:acul-preview', {
          detail: { screen: 'reset-password', action: 'submit' },
        }),
      )
      return 'preview'
    },
    [sdk, minLength],
  )

  const goToLogin = useCallback(async () => {
    const instance = sdk ?? (await createSdk())
    if (instance?.backToLogin) {
      await instance.backToLogin()
      return
    }
    window.location.search = '?screen=login-id'
  }, [sdk])

  return {
    loaded,
    handleSubmit,
    goToLogin,
    minLength,
    username: sdk?.screen?.data?.username,
    texts: sdk?.screen?.texts,
    serverError: classifyServerErrors(sdk?.getErrors?.()),
  }
}
